<script setup lang="ts">
import { fournirRegimeFiscal } from "~/composables/useRegimeFiscal";
/**
 * NOUVEAU MODÈLE de la page cagnotte (Karim 2026-10-12/13, inspiré de
 * dift.com) : haut de page selon la mise en page (`modele` : « immersif »
 * retenue, « mosaique » / « banniere » gardées), récit en sections avec
 * sommaire collant, donateurs, photos, actualités, « à quoi serviront les
 * dons », le mot de l'association, puis l'association en bas.
 *
 * Don : sur ordinateur, la carte de droite (choix du montant DANS la page,
 * paiement sur /c/<slug>/payment?montant= dans un nouvel onglet) ; sur
 * téléphone, la barre du bas qui se transforme en panneau avec clavier.
 *
 * La page (`pages/c/[slug]/index.vue`) garde ce qui est commun à tous les
 * modèles : chargement, référencement, aperçu du dashboard, états d'erreur.
 */
import { recuFiscalAnnonce } from "~/utils/recuFiscal";
import { nettoyerMontantsImpact } from "~/utils/montantsImpact";
import { navLinks } from "~/utils/navLinks";
import { useDemoAccueil, useDonneeDemoAccueil } from "~/composables/useDemoAccueil";
import { defilerDansDemoAccueil, zoneDemoAccueil } from "~/utils/demoAccueil";
import type { Campaign } from "~/types/campaign";
import type { useCampaignPublic } from "~/composables/useCampaignPublic";
import PageCagnotteHero from "./PageCagnotteHero.vue";
import PageCagnotteContenu from "./PageCagnotteContenu.vue";
import PageCagnotteCarteDon from "./PageCagnotteCarteDon.vue";
import PageCagnotteCoupDeCoeur from "./PageCagnotteCoupDeCoeur.vue";
import PageCagnotteCitation from "./PageCagnotteCitation.vue";
import PageCagnotteReseaux from "./PageCagnotteReseaux.vue";
import PageCagnotteAssociationBas from "./PageCagnotteAssociationBas.vue";
import PageCagnotteBanniere from "./PageCagnotteBanniere.vue";
import PageCagnotteSuiviCollecte from "./PageCagnotteSuiviCollecte.vue";
import PageCagnotteBarreDon from "./PageCagnotteBarreDon.vue";
import type {
  ActualiteCagnotte,
  AssociationCagnotte,
  CagnotteAffichee,
  ModelePageCagnotte,
  TemoinCagnotte,
} from "./types";

const props = defineProps<{
  /** La cagnotte affichée (brouillon du dashboard compris en aperçu). */
  campagne: Campaign;
  controleur: ReturnType<typeof useCampaignPublic>;
  modele: ModelePageCagnotte;
}>();

const {
  donorsSortBy,
  allDonors,
  donorsTotal,
  isLoadingDonors,
  isLoadingMoreDonors,
  topDonor,
  hasMoreDonors,
  formatAmountShort,
  formatAmountCompact,
  getRelativeTime,
  loadMoreDonors,
  closesAt,
  endDateLabel,
  marquerTerminee,
  isCampaignClosed,
  isCampaignPaused,
  etatDonCagnotte,
  closedDateText,
  youtubeEmbedUrl,
} = props.controleur;

const cagnotte = computed(() => props.campagne as unknown as CagnotteAffichee);
/** Démo de la home : pas de header (la fenêtre simulée en tient lieu). */
const { estIntegree } = useDemoAccueil();
const slug = computed(() => props.campagne.slug);
const config = useRuntimeConfig();
const api = config.public.apiBaseUrl || "https://api.donoor.org";

// ─── Réglages d'affichage de la cagnotte (comme l'ancienne page) ────────
const afficherDonateurs = computed(() => props.campagne.show_donors_list !== false);
const afficherMontant = computed(() => props.campagne.show_amount_raised !== false);

// ─── L'association et la page de paiement de la cagnotte ────────────────
const { data: association } = await useFetch(
  () => `${api}/api/organizations/public/slug/${props.campagne.organization_slug}`,
  {
    ...useDonneeDemoAccueil(`page-cagnotte-asso-${props.campagne.organization_slug}`),
    transform: (r: { data: AssociationCagnotte }) => r.data,
  },
);

// Données de la PAGE DE PAIEMENT de la cagnotte (même source que
// /c/<slug>/payment) : montants proposés, montant par défaut, paliers, forme
// des boutons, et le verdict fiscal (fermé par défaut, cf. recuFiscal.ts).
// Sa COULEUR personnalisée n'est PAS reprise : la page cagnotte est toujours
// au bleu Donoor (Karim 2026-10-13).
interface DonneesPaiement {
  showFiscalInfo?: boolean | null;
  paymentPage?: {
    showFiscalInfo?: boolean | null;
    amounts?: number[] | null;
    defaultAmountIndex?: number | null;
    primaryColor?: string | null;
    buttonStyle?: string | null;
  } | null;
  campaign?: { impact_options?: unknown } | null;
}
const { data: paiement } = await useFetch(() => `${api}/api/campaigns/public/${slug.value}/donation`, {
  ...useDonneeDemoAccueil(`page-cagnotte-paiement-${slug.value}`),
  transform: (r: { data: DonneesPaiement }) => ({
    fiscal: recuFiscalAnnonce(r.data),
    regimeFiscal: (r.data as { fiscalRegime?: string | null } | undefined)?.fiscalRegime ?? null,
    montants: r.data?.paymentPage?.amounts ?? [],
    indexDefaut: r.data?.paymentPage?.defaultAmountIndex ?? 0,
    styleBouton: r.data?.paymentPage?.buttonStyle ?? null,
    impacts: nettoyerMontantsImpact(r.data?.campaign?.impact_options),
  }),
});
const fiscal = computed(() => paiement.value?.fiscal === true);
// Mention du pays (66 %, 30 % en Belgique, déduction au Luxembourg) — 2026-10-30.
fournirRegimeFiscal(() => paiement.value?.regimeFiscal);
/**
 * Paliers « Ce que permet votre don » : ceux de la cagnotte affichée (le
 * brouillon du dashboard en aperçu), sinon ceux de la page de paiement.
 */
const impacts = computed(() => {
  const brouillon = nettoyerMontantsImpact((props.campagne as { impact_options?: unknown }).impact_options);
  return brouillon.length ? brouillon : (paiement.value?.impacts ?? []);
});

// ─── Contenus ───────────────────────────────────────────────────────────
/** Photos de la cagnotte (bannière à flèches de « banniere » ; rangée « Photos »). */
const images = computed(() => {
  const c = cagnotte.value;
  const ids = [c.image, ...(c.gallery ?? []).map((g) => g.directus_files_id)];
  return [...new Set(ids.filter((x): x is string => !!x))];
});
/** Rangée « Photos » sous les dons : seulement s'il y a une galerie (la
 *  bannière seule ferait doublon avec le haut de la page). */
const photosRangee = computed(() => ((cagnotte.value.gallery ?? []).length ? images.value : []));
/**
 * Mise en page réellement affichée : SANS aucune photo, pas de bannière
 * (Karim 2026-10-13) — « immersif » et « banniere » prennent le haut simple
 * de « mosaique » (titre, association), et la carte de don ne remonte plus
 * sur une bannière absente.
 */
const modeleAffiche = computed<ModelePageCagnotte>(() =>
  images.value.length || props.modele === "mosaique" ? props.modele : "mosaique",
);
const actualites = computed<ActualiteCagnotte[]>(
  () => (cagnotte.value.campaign_updates ?? []) as ActualiteCagnotte[],
);
const utilisationDons = computed(() => cagnotte.value.donation_uses ?? "");

/**
 * Le mot de l'association (migration 336) : la personne nommée, sinon
 * l'association elle-même (son nom, son logo). Rien sans message.
 */
const temoin = computed<TemoinCagnotte | null>(() => {
  const c = cagnotte.value;
  const message = c.spokesperson_message?.trim();
  if (!message) return null;
  const nom = c.spokesperson_name?.trim();
  return {
    message,
    nom: nom || c.organization_name,
    role: nom ? (c.spokesperson_role?.trim() || null) : null,
    photo: c.spokesperson_photo || (nom ? null : c.organization_logo),
    linkedin: c.spokesperson_linkedin || null,
  };
});
/**
 * « Coup de cœur Donoor » (migration 338) : écrit par l'équipe Donoor depuis
 * le backup-manager (fiche association › Services › Cagnottes). Vide = pas
 * d'encadré.
 */
const coupDeCoeur = computed(() => cagnotte.value.donoor_coup_de_coeur?.trim() ?? "");

// ─── Don ────────────────────────────────────────────────────────────────
/** Barre du bas (mobile) : « Faire un don » ailleurs dans la page ouvre son panneau. */
const barreDon = ref<{ ouvrir: () => void } | null>(null);
function allerAuDon() {
  // Sur téléphone, pas de carte de don dans la page : le panneau du bas.
  if (!window.matchMedia("(min-width: 1024px)").matches) {
    barreDon.value?.ouvrir();
    return;
  }
  // La carte elle-même, pas le haut de son bloc (vide au-dessus en « immersif »).
  const carte = elementCarte() ?? document.getElementById("don");
  if (carte && !defilerDansDemoAccueil(carte)) carte.scrollIntoView({ behavior: "smooth", block: "start" });
}

// La carte de don est-elle à l'écran ? Sur téléphone elle n'existe pas : la
// barre du bas est donc toujours là.
const formulaireVisible = ref(true);
let observateur: IntersectionObserver | null = null;
function observerFormulaire() {
  observateur?.disconnect();
  const el = document.getElementById("don");
  if (!el) return;
  observateur = new IntersectionObserver(([e]) => (formulaireVisible.value = !!e?.isIntersecting), {
    threshold: 0.05,
  });
  observateur.observe(el);
}
onMounted(observerFormulaire);
watch(modeleAffiche, () => nextTick(observerFormulaire));
onBeforeUnmount(() => observateur?.disconnect());

// Carte collante CENTRÉE verticalement dans l'écran, sans jamais passer sous
// la barre de navigation du site quand elle est visible (elle se cache en
// descendant). Jamais de défilement DANS la carte. Plus haute que l'écran :
// elle se cale par le BAS, pour que le bouton de paiement reste atteignable.
// Démo de la home : l'« écran » est la zone de la démo, sans barre du site.
const blocDon = ref<HTMLElement | null>(null);
const carteDon = ref<{ $el?: HTMLElement } | null>(null);
/** La carte elle-même (le bloc collant peut être plus haut qu'elle, cf. template). */
const elementCarte = () => carteDon.value?.$el ?? blocDon.value;
const hautCollant = ref(24);
let cadre = 0;
function placerCarte() {
  cancelAnimationFrame(cadre);
  cadre = requestAnimationFrame(() => {
    const zone = zoneDemoAccueil(blocDon.value);
    const barre = zone ? null : document.querySelector("header");
    const visible =
      !!barre && !barre.classList.contains("pointer-events-none") && getComputedStyle(barre).opacity !== "0";
    const bas = visible && barre ? barre.getBoundingClientRect().bottom : 0;
    const sousLaBarre = Math.max(24, Math.round(bas + 16));
    const hauteurCarte = elementCarte()?.offsetHeight ?? 0;
    // « immersif » : une carte courte est calée en BAS d'un bloc plus haut
    // qu'elle, et une longue peut être remontée (décalage CSS, cf.
    // DECALAGE_BLOC_IMMERSIF). On place la CARTE : le vide au-dessus d'elle et
    // le décalage du bloc se retranchent de la position collante.
    const vide = Math.max(0, (blocDon.value?.offsetHeight ?? 0) - hauteurCarte);
    const transformation = blocDon.value ? getComputedStyle(blocDon.value).transform : "none";
    const decalage = transformation && transformation !== "none" ? new DOMMatrixReadOnly(transformation).m42 : 0;
    const fenetre = zone ? zone.clientHeight : window.innerHeight;
    const haut =
      hauteurCarte + sousLaBarre + 16 <= fenetre
        ? Math.max(sousLaBarre, Math.round((fenetre - hauteurCarte) / 2))
        : Math.min(sousLaBarre, Math.round(fenetre - hauteurCarte - 16));
    hautCollant.value = Math.round(haut - vide - decalage);
  });
}
/**
 * « immersif » : hauteur minimale du bloc de la carte, qui fait DESCENDRE une
 * carte courte pour qu'elle déborde UN PEU sous la photo (Karim 2026-10-20 :
 * au ras du bas de la photo « ça fait moche », 200 px « beaucoup trop bas »).
 * Haut de la colonne = bas de la photo + 40 (marge) − 480 (remontée) : une
 * carte de hauteur h déborde de h − 440 px.
 *  - 500 = 440 + 60 de débordement ;
 *  - 100vh + 280px − hauteur de la photo : le bas de la carte reste dans
 *    l'écran à l'ouverture (bouton « Faire un don » visible), avec 24 px de
 *    marge — la photo fait clamp(440px, 62vh, 640px) et commence à 112 + 24 px.
 * Le plus petit des deux. Sans effet sur une carte plus haute que ce minimum.
 */
const HAUTEUR_MIN_BLOC_IMMERSIF = "min(500px, calc(100vh + 280px - clamp(440px, 62vh, 640px)))";
/**
 * « immersif » : une carte LONGUE qui serait coupée en bas de l'écran à
 * l'ouverture REMONTE juste ce qu'il faut (Karim 2026-10-20, cagnotte avec
 * paliers + reçu fiscal : « ça va trop bas donc ça coupe »), sans jamais
 * dépasser le haut de la photo. `100%` = la hauteur du bloc lui-même : le
 * décalage se calcule sans script, la carte est à sa place dès le premier
 * affichage. Remontée maximale : 440px − hauteur de la photo (le haut de la
 * colonne est à hauteur de photo − 440 px sous son haut). Rien dans la carte
 * n'est en `position: fixed` (un décalage en ferait son repère).
 */
const DECALAGE_BLOC_IMMERSIF =
  "translateY(min(0px, max(calc(100vh + 280px - clamp(440px, 62vh, 640px) - 100%), calc(440px - clamp(440px, 62vh, 640px)))))";
const styleCollant = computed(() => ({
  top: `${hautCollant.value}px`,
  // Sous `lg`, la colonne est masquée : ces deux règles n'y jouent pas.
  ...(modeleAffiche.value === "immersif"
    ? { minHeight: HAUTEUR_MIN_BLOC_IMMERSIF, transform: DECALAGE_BLOC_IMMERSIF }
    : {}),
}));
// La barre change d'état un instant APRÈS le défilement (elle est
// temporisée) : on suit aussi ses changements de classe.
let observateurBarre: MutationObserver | null = null;
let observateurTaille: ResizeObserver | null = null;
let defilement: HTMLElement | Window | null = null;
onMounted(() => {
  placerCarte();
  const zone = zoneDemoAccueil(blocDon.value);
  defilement = zone ?? window;
  defilement.addEventListener("scroll", placerCarte, { passive: true });
  window.addEventListener("resize", placerCarte);
  if (blocDon.value) {
    observateurTaille = new ResizeObserver(placerCarte);
    observateurTaille.observe(blocDon.value);
    // La carte AUSSI : courte, elle change de taille sans que son bloc (640 px
    // minimum en « immersif ») ne bouge.
    const carte = elementCarte();
    if (carte && carte !== blocDon.value) observateurTaille.observe(carte);
  }
  const barre = zone ? null : document.querySelector("header");
  if (barre) {
    observateurBarre = new MutationObserver(placerCarte);
    observateurBarre.observe(barre, { attributes: true, attributeFilter: ["class"] });
  }
});
onBeforeUnmount(() => {
  observateurBarre?.disconnect();
  observateurTaille?.disconnect();
  defilement?.removeEventListener("scroll", placerCarte);
  window.removeEventListener("resize", placerCarte);
  cancelAnimationFrame(cadre);
});

const partageOuvert = ref(false);
const dernierDon = computed(() => {
  const d = allDonors.value[0] as { created_at?: string; date_created?: string } | undefined;
  const date = d?.created_at ?? d?.date_created;
  return date ? getRelativeTime(date) : null;
});
</script>

<template>
  <div class="grow bg-white">
    <AppHeader v-if="!estIntegree" :links="navLinks" rayon="cagnottes" />

    <!-- pt : place de la barre de navigation fixe du site (démo de la home :
         pas de barre) ; pb (mobile) : place de la barre de don, en bas.
         « immersif » sur téléphone : AUCUNE marge en haut — la photo part du
         bord de l'écran et la barre de navigation flotte dessus (Karim
         2026-10-13 : « la bannière doit être collée tout en haut ») ; dans
         la démo de l'accueil aussi, sous l'îlot du téléphone simulé. -->
    <div
      class="pb-60 lg:pb-16"
      :class="[
        estIntegree ? 'lg:pt-8' : 'lg:pt-28',
        modeleAffiche === 'immersif' ? 'pt-0' : estIntegree ? 'pt-6' : 'pt-24',
      ]"
    >
      <div class="max-w-[1240px] mx-auto px-5 sm:px-8">
        <PageCagnotteHero :cagnotte="cagnotte" :variante="modeleAffiche" :sous-barre="!estIntegree" />

        <div
          class="grid grid-cols-[minmax(0,1fr)] lg:grid-cols-[minmax(0,1fr)_400px] gap-10 lg:gap-14"
          :class="modeleAffiche === 'banniere' ? 'mt-7' : 'mt-6 lg:mt-10'"
        >
          <div class="order-1 min-w-0">
            <!-- « banniere » : la bannière à flèches ouvre la colonne de gauche -->
            <PageCagnotteBanniere v-if="modeleAffiche === 'banniere'" :images="images" class="mb-4" />
            <!-- Donateurs : sous la bannière (la carte de droite reste légère) -->
            <PageCagnotteSuiviCollecte
              class="mb-2"
              :donateurs="afficherDonateurs ? (allDonors as Array<{ id: string; name?: string | null }>) : []"
              :nb-donateurs="Number(cagnotte.donors_count) || donorsTotal"
              :dernier-don="dernierDon"
            />
            <PageCagnotteContenu
              :cagnotte="cagnotte"
              :actualites="actualites"
              :utilisation-dons="utilisationDons"
              :total-donateurs="afficherDonateurs ? donorsTotal : 0"
              :photos="photosRangee"
              :avec-association="!!association"
              :video-url="youtubeEmbedUrl"
            >
              <!-- Coup de cœur Donoor + mot d'une personne de l'asso -->
              <template v-if="coupDeCoeur || temoin" #apres-donateurs>
                <!-- Les deux cadres ont la MÊME hauteur (grille étirée) ; « Suivre
                     l'association » passe dessous, hors des cadres. -->
                <div class="mt-16 grid md:grid-cols-2 gap-6 items-stretch">
                  <PageCagnotteCoupDeCoeur v-if="coupDeCoeur" :texte="coupDeCoeur" class="h-full" />
                  <!-- Seul (pas de coup de cœur) : centré, à la même largeur. -->
                  <PageCagnotteCitation
                    v-if="temoin"
                    :temoin="temoin"
                    class="h-full"
                    :class="coupDeCoeur ? '' : 'md:col-span-2 md:w-1/2 md:mx-auto'"
                  />
                  <PageCagnotteReseaux
                    v-if="association"
                    :liens="association.social_links"
                    :site="association.website"
                    class="px-2"
                    :class="coupDeCoeur && temoin ? 'md:col-start-2' : 'md:col-span-2 md:w-1/2 md:mx-auto'"
                  />
                </div>
              </template>
              <template #donateurs>
                <CampaignDonorsList
                  v-model:sort-by="donorsSortBy"
                  :donors="allDonors"
                  :donors-total="donorsTotal"
                  :top-donor="topDonor"
                  :is-loading="isLoadingDonors"
                  :is-loading-more="isLoadingMoreDonors"
                  :has-more="hasMoreDonors"
                  :format-amount-short="formatAmountShort"
                  :get-relative-time="getRelativeTime"
                  @load-more="loadMoreDonors"
                />
              </template>
            </PageCagnotteContenu>
          </div>

          <!-- Colonne droite (ordinateur) : la carte de paiement SUIT le
               défilement. min-w-0 : le rail des paliers ne doit pas élargir la
               colonne. z-[60] : TOUJOURS devant le chat (z 50). Sur téléphone, la
               barre du bas et son panneau de don la remplacent. -->
          <aside
            class="hidden lg:block order-2 relative z-[60] min-w-0"
            :class="modeleAffiche === 'immersif' ? 'lg:-mt-[480px] lg:translate-x-12' : ''"
          >
            <!-- « immersif » : la carte DÉBORDE toujours sous la photo (Karim
                 2026-10-20 — courte, elle s'arrêtait au ras du bas de la photo).
                 Le bloc a une hauteur minimale (cf. HAUTEUR_MIN_BLOC_IMMERSIF) et
                 une carte plus courte s'y cale EN BAS ; plus longue, elle agrandit
                 le bloc et ne bouge pas. En CSS : la carte est à sa place dès le
                 premier affichage, sans saut. -->
            <div
              id="don"
              ref="blocDon"
              class="scroll-mt-28 lg:sticky lg:transition-[top] lg:duration-300 lg:ease-out"
              :class="modeleAffiche === 'immersif' ? 'lg:flex lg:flex-col' : ''"
              :style="styleCollant"
            >
              <PageCagnotteCarteDon
                ref="carteDon"
                class="scroll-mt-28"
                :class="modeleAffiche === 'immersif' ? 'lg:mt-auto' : ''"
                :cagnotte="cagnotte"
                :fiscal="fiscal"
                :montants="paiement?.montants ?? []"
                :index-defaut="paiement?.indexDefaut ?? 0"
                :impacts="impacts"
                :style-bouton="paiement?.styleBouton"
                :closes-at="closesAt"
                :end-date-label="endDateLabel"
                :en-pause="isCampaignPaused"
                :terminee="isCampaignClosed"
                :etat-don="etatDonCagnotte"
                :date-fin="closedDateText"
                :afficher-montant="afficherMontant"
                @terminee="marquerTerminee"
                @partager="partageOuvert = true"
              />
            </div>
          </aside>
        </div>

        <!-- L'association, en bas, centrée -->
        <PageCagnotteAssociationBas
          v-if="association"
          :association="association"
          :slug-cagnotte="slug"
          :donateurs-cagnotte="donorsTotal"
          :dons-fermes="!!etatDonCagnotte"
          @donner="allerAuDon"
        />
      </div>
    </div>

    <!-- Téléphone : barre du bas (montant, plus gros don, date limite) ;
         « Faire un don » la transforme en panneau avec clavier. -->
    <PageCagnotteBarreDon
      ref="barreDon"
      :cagnotte="cagnotte"
      :visible="!formulaireVisible"
      :plus-gros-don="afficherDonateurs && afficherMontant ? topDonor : null"
      :format-amount-short="formatAmountShort"
      :format-amount-compact="formatAmountCompact"
      :en-pause="isCampaignPaused"
      :terminee="isCampaignClosed"
      :etat-don="etatDonCagnotte"
      :date-fin="closedDateText"
      :closes-at="closesAt"
      :end-date-label="endDateLabel"
      :afficher-montant="afficherMontant"
      :fiscal="fiscal"
      :montants="paiement?.montants ?? []"
      :index-defaut="paiement?.indexDefaut ?? 0"
      :impacts="impacts"
      @partager="partageOuvert = true"
    />

    <!-- La fenêtre de partage habituelle de la page cagnotte -->
    <CampaignShareBottomSheet v-model:open="partageOuvert" :campaign="campagne" />
  </div>
</template>
